import React, { useMemo } from 'react';
import { Card, Pill } from '../ui';
import { useCop } from '../CopApp';

// Builds the account-wide suppression policy from the configured guardrails.
// This policy is applied to every campaign *before* per-campaign company_json
// targeting — a campaign can narrow the audience further but never re-include
// anyone suppressed here. Lists stay plain (no invented IDs / hashes).
export function buildSuppressionPolicy(s) {
	const list = (v) => v.split(',').map((x) => x.trim()).filter(Boolean);
	const policy = { scope: 'account', applies: 'all_campaigns' };

	if (s.suppressedPages) policy.suppressed_pages = list(s.suppressedPages);
	if (s.suppressCookies) policy.suppress_cookies = list(s.suppressCookies);
	if (s.suppressedCountries) policy.suppressed_countries = list(s.suppressedCountries).map((c) => c.toLowerCase());
	if (s.suppressedStates) policy.suppressed_states = list(s.suppressedStates).map((x) => x.toUpperCase());
	if (s.excludedSegments) policy.excluded_segments = list(s.excludedSegments);
	if (s.trafficSourceExcludes) policy.suppressed_traffic_sources = list(s.trafficSourceExcludes);

	const caps = {};
	if (s.frequencyCapDay !== '') caps.per_day = Number(s.frequencyCapDay);
	if (s.perSessionCap !== '') caps.per_session = Number(s.perSessionCap);
	if (Object.keys(caps).length) policy.frequency_cap = caps;

	const cooldown = {};
	if (s.postConversionCooldownDays !== '') cooldown.post_conversion_days = Number(s.postConversionCooldownDays);
	if (s.postDismissCooldownHrs !== '') cooldown.post_dismiss_hours = Number(s.postDismissCooldownHrs);
	if (Object.keys(cooldown).length) policy.cooldown = cooldown;

	if (s.globalHoldoutPercent !== '') policy.global_holdout_percent = Number(s.globalHoldoutPercent);
	if (s.suppressLoggedIn) policy.suppress_logged_in = true;
	if (s.suppressActiveCheckout) policy.suppress_active_checkout = true;
	if (s.honorGpc) policy.honor_gpc = true;
	if (s.quietHoursStart && s.quietHoursEnd) policy.quiet_hours = { start: s.quietHoursStart, end: s.quietHoursEnd, tz: 'America/Los_Angeles' };

	return { suppressions: policy };
}

function Field({ label, children, span }) {
	return (
		<label className={`cop-form-field${span ? ' cop-form-span' : ''}`}>
			<span>{label}</span>
			{children}
		</label>
	);
}

export default function GlobalSuppressions() {
	const { suppressions, setSuppressions, showToast } = useCop();
	const set = (key) => (e) => setSuppressions((s) => ({ ...s, [key]: e.target.value }));
	const toggle = (key) => () => setSuppressions((s) => ({ ...s, [key]: !s[key] }));

	const json = useMemo(() => JSON.stringify(buildSuppressionPolicy(suppressions), null, 2), [suppressions]);

	const copyJson = () => {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(json).then(() => showToast('Suppression policy copied'));
		} else {
			showToast('Copy not available');
		}
	};

	return (
		<main className="cop-page">
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
				<div style={{ flex: 1, minWidth: 260 }}>
					<h1 className="cop-page-title">
						Global Suppressions <Pill tone="amber">Account guardrails</Pill>
					</h1>
					<div className="cop-page-sub">
						Applied to every campaign before per-campaign targeting. A campaign can narrow further, but can never re-include anyone
						suppressed here.
					</div>
				</div>
				<button className="cop-btn primary" onClick={() => showToast('Suppression policy saved')}>Save Policy</button>
			</div>

			<div className="cop-grid cop-builder-rules" style={{ marginTop: 16 }}>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					<Card title="Pages & Cookies" icon="⊘" right="Where campaigns never render">
						<div className="cop-form-grid">
							<Field label="Suppressed pages" span>
								<input className="cop-input" value={suppressions.suppressedPages} onChange={set('suppressedPages')} placeholder="/account, /login" />
							</Field>
							<Field label="Suppress when cookie present" span>
								<input className="cop-input" value={suppressions.suppressCookies} onChange={set('suppressCookies')} placeholder="usi_converted, usi_optout" />
							</Field>
							<div className="cop-config-row cop-form-span">
								<span className="k">Suppress logged-in visitors</span>
								<button className={`cop-toggle${suppressions.suppressLoggedIn ? ' on' : ''}`} onClick={toggle('suppressLoggedIn')} aria-label="toggle suppress logged-in" />
							</div>
							<div className="cop-config-row cop-form-span">
								<span className="k">Suppress during active checkout</span>
								<button className={`cop-toggle${suppressions.suppressActiveCheckout ? ' on' : ''}`} onClick={toggle('suppressActiveCheckout')} aria-label="toggle suppress active checkout" />
							</div>
						</div>
					</Card>

					<Card title="Audience Exclusions" icon="◎">
						<div className="cop-form-grid">
							<Field label="Excluded segments" span>
								<input className="cop-input" value={suppressions.excludedSegments} onChange={set('excludedSegments')} placeholder="Loyal Customers, Recent Purchasers" />
							</Field>
							<Field label="Suppressed traffic sources" span>
								<input className="cop-input" value={suppressions.trafficSourceExcludes} onChange={set('trafficSourceExcludes')} placeholder="paid_brand, affiliate" />
							</Field>
							<Field label="Suppressed countries">
								<input className="cop-input" value={suppressions.suppressedCountries} onChange={set('suppressedCountries')} placeholder="cn, ru" />
							</Field>
							<Field label="Suppressed states">
								<input className="cop-input" value={suppressions.suppressedStates} onChange={set('suppressedStates')} placeholder="CA" />
							</Field>
						</div>
					</Card>

					<Card title="Frequency & Cooldown" icon="⏱">
						<div className="cop-form-grid">
							<Field label="Max impressions / day">
								<input className="cop-input" type="number" min="0" value={suppressions.frequencyCapDay} onChange={set('frequencyCapDay')} placeholder="3" />
							</Field>
							<Field label="Max impressions / session">
								<input className="cop-input" type="number" min="0" value={suppressions.perSessionCap} onChange={set('perSessionCap')} placeholder="1" />
							</Field>
							<Field label="Cooldown after conversion (days)">
								<input className="cop-input" type="number" min="0" value={suppressions.postConversionCooldownDays} onChange={set('postConversionCooldownDays')} placeholder="30" />
							</Field>
							<Field label="Cooldown after dismiss (hrs)">
								<input className="cop-input" type="number" min="0" value={suppressions.postDismissCooldownHrs} onChange={set('postDismissCooldownHrs')} placeholder="24" />
							</Field>
							<Field label="Quiet hours — start">
								<input className="cop-input" type="time" value={suppressions.quietHoursStart} onChange={set('quietHoursStart')} />
							</Field>
							<Field label="Quiet hours — end">
								<input className="cop-input" type="time" value={suppressions.quietHoursEnd} onChange={set('quietHoursEnd')} />
							</Field>
						</div>
					</Card>

					<Card title="Measurement & Privacy" icon="⚙">
						<div className="cop-form-grid">
							<Field label={`Global holdout — ${suppressions.globalHoldoutPercent}%`} span>
								<input type="range" min="0" max="20" value={suppressions.globalHoldoutPercent} onChange={set('globalHoldoutPercent')} />
							</Field>
							<div className="cop-config-row cop-form-span">
								<span className="k">Honor GPC / opt-out signals</span>
								<button className={`cop-toggle${suppressions.honorGpc ? ' on' : ''}`} onClick={toggle('honorGpc')} aria-label="toggle honor GPC" />
							</div>
						</div>
					</Card>
				</div>

				<Card title="suppression policy" icon="{ }" right={<button className="cop-btn small" onClick={copyJson}>Copy</button>}>
					<pre className="cop-json-preview">{json}</pre>
					<div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6 }}>
						The global holdout reserves a slice of traffic from <i>all</i> campaigns so account-level incrementality stays measurable.
						Quiet hours and cooldowns are evaluated in <span className="cop-mono">America/Los_Angeles</span>.
					</div>
				</Card>
			</div>
		</main>
	);
}
